import { useContext, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  getCustomerReportAction,
  SET_CURRENT_STAGE,
  SET_CURRENT_STAGE_ATTRIBUTES,
} from 'actions/grace';
import { CustomerReportContext } from 'components/grace/Contexts';
import useAnalyticsCallback from 'hooks/useAnalyticsCallback';
import useAnalyticsEffect from 'hooks/useAnalyticsEffect';
import {
  MODAL_TYPES,
  STAGE_TYPES,
  GENERIC_ERROR,
  HELP_WITH_A_DIFFERENT_ORDER,
  CANCELLATION_STAGE_SLUG,
  LIST_OPTIONS_SELECT_SLUG,
  TRACKING_LINK_SLUGS,
} from 'lib/constants';
import * as fromGrace from 'reducers/grace';
import * as ErrorLogging from 'services/ErrorLogging';

import GraceAdditionalSteps from './AdditionalSteps';
import IngredientsList from './IngredientsList';
import GraceListHeader from './ListHeader';
import GraceListRowButton from './ListRowButton';
import ListRowLinkGroup from './ListRowLinkGroup';
import GraceLoadingScreen from './LoadingScreen';
import TextBoxPage from './TextBoxPage';
import GraceTrackingLinks from './TrackingLinks';

const GraceForm = () => {
  const {
    actionsTaken,
    setActionsTaken,
    setErrorMessage,
    selectedBasket,
    setSelectedBasket,
    setIngredient,
    ingredient,
    setMeal,
    meal,
    setCurrentModal,
    closeOutdatedModal,
  } = useContext(CustomerReportContext);

  const customerReport = useSelector(state => fromGrace.selectCustomerReport(state));
  const currentStage = useSelector(state => fromGrace.selectCurrentStage(state));
  const availableBaskets = useSelector(state => fromGrace.selectBasketsWithMeals(state));
  const nextStageOptions = useSelector(state => fromGrace.selectNextStageOptions(state));
  const ingredientOptions = useSelector(state => fromGrace.selectIngredientOptions(state));
  const recipeCardLink = useSelector(state => fromGrace.selectRecipeCardLink(state));

  const [isLoading, setIsLoading] = useState(false);
  const itemSelectionStageRef = useRef(null);
  const submissionInFlightRef = useRef(false);
  const dispatch = useDispatch();

  const releaseSubmissionGuard = () => {
    submissionInFlightRef.current = false;
  };

  const showTrackingLink = TRACKING_LINK_SLUGS.includes(currentStage.slug);

  useAnalyticsEffect(
    ({ events, logEvent }) => {
      logEvent(events.grace.stageViewed, { title: currentStage.title });
    },
    [currentStage.title]
  );

  useEffect(() => {
    if (availableBaskets?.length > 0) {
      setSelectedBasket(availableBaskets[0]);
    }
  }, [availableBaskets]);

  useEffect(() => {
    if (
      !availableBaskets?.length ||
      (currentStage.stageType !== STAGE_TYPES.optionsWithSelectedBasket &&
        currentStage.stageType !== STAGE_TYPES.mealSelection)
    ) {
      return;
    }

    if (!itemSelectionStageRef.current) {
      itemSelectionStageRef.current = {
        title: currentStage.title,
        subtitle: currentStage.subtitle,
      };
    }

    const newStage = {
      title: selectedBasket ? itemSelectionStageRef.current.title : 'Which delivery had a problem?',
      subtitle: selectedBasket ? itemSelectionStageRef.current.subtitle : '',
    };

    dispatch({ type: SET_CURRENT_STAGE_ATTRIBUTES, payload: newStage });
  }, [selectedBasket, availableBaskets, currentStage.stageType]); // Intentionally not including currentStage as a dependency as a stop gap to refactoring this logic for better redux support

  const isCustomerInputStage = () => {
    return currentStage && currentStage.stageType === 'customer_input';
  };

  const isIngredientSelectionStage = () => {
    return currentStage.stageType === STAGE_TYPES.ingredientSelection;
  };

  const isResolutionStage = () => {
    return currentStage.stageType == STAGE_TYPES.resolution;
  };

  const hasOptionsWithSelectedBasket = () => {
    return currentStage.stageType == STAGE_TYPES.optionsWithSelectedBasket;
  };

  const updateStateForStages = (resolver, reportStageOption) => {
    resolver.nextStage && dispatch({ type: SET_CURRENT_STAGE, payload: resolver.nextStage });
    setActionsTaken([
      ...actionsTaken,
      {
        customerReportId: customerReport.id,
        reportStageOption: reportStageOption,
        currentStage: customerReport.nextStage,
      },
    ]);
  };

  // Handler Methods
  const handleOnClick = useAnalyticsCallback(
    async (reportStageOption, incidentInfo = {}) => {
      if (submissionInFlightRef.current) {
        return;
      }
      submissionInFlightRef.current = true;

      try {
        const result = await dispatch(
          getCustomerReportAction({
            customerReportId: customerReport.id,
            reportStageOptionId: reportStageOption.id,
            incidentInfo,
          })
        );

        if (!result.ok && !result.resolver?.priorClose) {
          setErrorMessage(GENERIC_ERROR);
          ErrorLogging.captureException(result.error?.message || result.error);
          releaseSubmissionGuard();
          return;
        } else {
          setErrorMessage(null);
        }

        const { resolver } = result;

        if (resolver.isAdaResolution) {
          setCurrentModal(MODAL_TYPES.ada);
          releaseSubmissionGuard();
        } else if (resolver.isCsResolution) {
          setCurrentModal(MODAL_TYPES.cs);
          releaseSubmissionGuard();
        } else if (resolver.priorClose === true) {
          closeOutdatedModal();
          releaseSubmissionGuard();
        } else {
          if (resolver?.nextStage?.stageType === 'resolution') {
            setIsLoading(true);
            setTimeout(() => {
              setIsLoading(false);
              updateStateForStages(resolver, reportStageOption);
              releaseSubmissionGuard();
            }, 1500);
          } else {
            updateStateForStages(resolver, reportStageOption);
            releaseSubmissionGuard();
          }
        }
      } catch (error) {
        setErrorMessage(GENERIC_ERROR);
        ErrorLogging.captureException(error);
        releaseSubmissionGuard();
      }
    },
    ({ callbackArgs, logEvent, events }) => {
      logEvent(events.grace.optionClicked, {
        name: callbackArgs[0].name,
        slug: callbackArgs[0].slug,
      });
    },
    [customerReport, actionsTaken]
  );

  const handleSelectAnotherDelivery = () => {
    setSelectedBasket(null);
  };

  const handleSelectMeal = useAnalyticsCallback(
    meal => {
      setMeal(meal);
      handleOnClick(nextStageOptions[0], { mealSelectionId: meal.mealSelectionId });
    },
    ({ logEvent, events, callbackArgs }) => {
      const [meal] = callbackArgs;
      logEvent(events.grace.mealClicked, {
        meal_name: meal.displayText,
      });
    }
  );

  const handleSelectIngredient = useAnalyticsCallback(
    ing => {
      setIngredient(ing);
      let incidentInfo = { mealSelectionId: meal.mealSelectionId };
      if (ing.id !== 'whole-meal') {
        incidentInfo.ingredientAssignmentId = ing.id;
      }
      handleOnClick(ing.option, incidentInfo);
    },
    ({ logEvent, events, callbackArgs }) => {
      const [ing] = callbackArgs;
      logEvent(events.grace.ingredientClicked, {
        ingredient_name: ing.displayText,
      });
    }
  );

  const handleSubmitCustomerInput = useAnalyticsCallback(
    customerInput => {
      let incidentInfo = { customer_input: customerInput };
      if (meal && meal.mealSelectionId) incidentInfo.mealSelectionId = meal.mealSelectionId;
      if (ingredient && ingredient.id !== 'whole-meal') {
        incidentInfo.ingredientAssignmentId = ingredient.id;
      }
      handleOnClick(nextStageOptions[0], incidentInfo);
    },
    ({ logEvent, events, callbackArgs }) => {
      const [customerInput] = callbackArgs;
      logEvent(events.grace.textSubmitted, {
        text_value: customerInput,
      });
    }
  );

  const handleSelectBasket = useAnalyticsCallback(
    b => setSelectedBasket(b),
    ({ logEvent, events, callbackArgs }) => {
      logEvent(events.grace.otherBasketSelected, {
        order: callbackArgs[0].deliveryDate,
      });
    }
  );

  // Render Methods
  const renderListHeader = () => {
    if (!selectedBasket || isResolutionStage() || currentStage.slug == CANCELLATION_STAGE_SLUG)
      return null;
    return (
      <GraceListHeader
        listTitle={`${selectedBasket.deliveryDate} Delivery`}
        linkText={availableBaskets?.length > 1 && HELP_WITH_A_DIFFERENT_ORDER}
        onClick={handleSelectAnotherDelivery}
      />
    );
  };

  const renderListRowLinkContent = () => {
    if (isIngredientSelectionStage() && ingredientOptions?.length > 0) {
      return (
        <IngredientsList
          ingredientOptions={ingredientOptions}
          nextStageOptions={nextStageOptions}
          handleSelectIngredient={handleSelectIngredient}
        />
      );
    }

    if (!availableBaskets || availableBaskets?.length === 0) {
      return null;
    }

    if (currentStage.stageType === STAGE_TYPES.options && selectedBasket) {
      return renderNextStageOptions();
    }

    return selectedBasket ? renderMealTitlesAndPhotos() : renderDeliveryDates();
  };

  const optionsAsButtons = options => {
    return (
      <section className="mx-4">
        {options.map(option => {
          return (
            <GraceListRowButton
              key={option.id}
              option={option}
              onClick={() => nextStageOptionOnClick(option)}
            />
          );
        })}
      </section>
    );
  };

  const optionsAsLinks = options => {
    return (
      <ListRowLinkGroup
        iterable={options}
        idKey="id"
        displayTextKey="name"
        onClick={nextStageOptionOnClick}
      />
    );
  };

  const nextStageOptionOnClick = option => {
    const incidentInfo = {};
    if (currentStage.slug == CANCELLATION_STAGE_SLUG) {
      incidentInfo['cancel'] = true;
    } else if (selectedBasket) {
      incidentInfo['basket_id'] = selectedBasket.id;
    }

    handleOnClick(option, incidentInfo);
  };

  const renderNextStageOptions = () => {
    if (
      isIngredientSelectionStage() ||
      (hasOptionsWithSelectedBasket() && selectedBasket === null) ||
      isCustomerInputStage()
    ) {
      return null;
    }

    return currentStage.slug === LIST_OPTIONS_SELECT_SLUG
      ? optionsAsLinks(nextStageOptions)
      : optionsAsButtons(nextStageOptions);
  };

  const renderListOptions = () => {
    if (nextStageOptions && !availableBaskets?.length > 0 && nextStageOptions.length > 0) {
      return renderNextStageOptions();
    } else if (nextStageOptions && nextStageOptions.length > 0 && hasOptionsWithSelectedBasket()) {
      return (
        <>
          <section className="mb-8">{renderListHeader()}</section>
          {renderNextStageOptions()}
        </>
      );
    }
  };

  const renderMealTitlesAndPhotos = () => {
    if (!selectedBasket || !selectedBasket.meals || hasOptionsWithSelectedBasket()) return null;

    return (
      <>
        {renderListHeader()}
        <ListRowLinkGroup
          iterable={selectedBasket.meals}
          idKey={'mealSelectionId'}
          displayTextKey={'displayText'}
          photoKey={'photo'}
          onClick={handleSelectMeal}
        />
      </>
    );
  };

  const renderDeliveryDates = () => {
    if (!availableBaskets) return null;

    return (
      <ListRowLinkGroup
        iterable={availableBaskets}
        idKey={'id'}
        displayTextKey={'deliveryDate'}
        onClick={handleSelectBasket}
      />
    );
  };

  const renderTrackingLinks = () => {
    if (!showTrackingLink) return null;
    if (!selectedBasket || selectedBasket?.trackingLinks.length === 0) return null;

    return <GraceTrackingLinks trackingLinks={selectedBasket.trackingLinks} />;
  };

  return (
    <>
      {renderListOptions()}
      {meal && recipeCardLink && isResolutionStage() && (
        <section className="mx-4">
          <GraceListRowButton
            key={meal.id}
            option={{ id: meal.id, name: 'View Recipe Card' }}
            onClick={() => window.location.replace(recipeCardLink)}
          />
        </section>
      )}
      {renderTrackingLinks()}
      {isCustomerInputStage() && <TextBoxPage onClick={handleSubmitCustomerInput} />}
      {isResolutionStage() && <GraceAdditionalSteps />}

      <section>{renderListRowLinkContent()}</section>
      {isLoading && <GraceLoadingScreen />}
    </>
  );
};

export default GraceForm;
